import { useState } from "react";
import { useTranslate } from "ra-core";
import { CheckCircle2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { PlaneKeyError } from "../providers/commons/planeKey";
import {
  planeKeyErrorMessage,
  useM_PlaneKey_Remove,
  useM_PlaneKey_Save,
  useQ_PlaneKey_Status,
} from "./usePlaneKey";

/**
 * The Profile page's Plane section: connect, replace or remove your own Plane
 * API key.
 *
 * Plane records whoever owns the key as a work item's creator, so every push
 * runs with the pusher's key and a person without one cannot push (LCCRM-92).
 * The key is write-only here: once saved, only "Connected as <email>" comes back.
 */
export const PlaneKeyCard = () => {
  const translate = useTranslate();
  const status = useQ_PlaneKey_Status();
  const [draft, setDraft] = useState("");
  const [replacing, setReplacing] = useState(false);
  const { mutation: save } = useM_PlaneKey_Save({
    onSuccess: () => {
      setDraft("");
      setReplacing(false);
    },
  });
  const { mutation: remove } = useM_PlaneKey_Remove();

  const connected = status.data?.connected === true;
  const showForm = !connected || replacing;

  const submit = () => {
    const key = draft.trim();
    if (key) save.mutate(key);
  };

  const handleRemove = () => {
    if (window.confirm(translate("crm.plane.key.remove_confirm"))) {
      remove.mutate();
    }
  };

  return (
    <Card>
      <CardContent>
        <h2 className="text-xl font-semibold text-muted-foreground mb-2">
          {translate("crm.plane.key.title")}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {translate("crm.plane.key.description")}
        </p>

        {status.isPending ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : status.isError ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.plane.key.unavailable")}
          </p>
        ) : (
          <div className="space-y-3">
            {connected && (
              <div className="flex flex-wrap items-center gap-2">
                <CheckCircle2 className="size-4 text-emerald-600" />
                <span className="text-sm font-medium" role="status">
                  {status.data?.connected && status.data.email
                    ? translate("crm.plane.key.connected_as", {
                        email: status.data.email,
                      })
                    : translate("crm.plane.key.connected")}
                </span>
                <span className="flex-1" />
                {!replacing && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setReplacing(true)}
                    >
                      {translate("crm.plane.key.replace")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemove}
                      disabled={remove.isPending}
                    >
                      {translate("crm.plane.key.remove")}
                    </Button>
                  </>
                )}
              </div>
            )}

            {showForm && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    // A secret: masked, and kept out of the browser's saved
                    // logins, which would otherwise offer to store it as the
                    // CRM password.
                    type="password"
                    autoComplete="off"
                    value={draft}
                    onChange={(event) => {
                      setDraft(event.target.value);
                      if (save.isError) save.reset();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        submit();
                      }
                    }}
                    aria-label={translate("crm.plane.key.label")}
                    aria-invalid={save.isError}
                    placeholder={translate("crm.plane.key.placeholder")}
                    className="max-w-md"
                    disabled={save.isPending}
                  />
                  <Button
                    type="button"
                    onClick={submit}
                    disabled={save.isPending || draft.trim() === ""}
                  >
                    {save.isPending && (
                      <Loader2 className="size-4 animate-spin" />
                    )}
                    {translate("crm.plane.key.connect")}
                  </Button>
                  {replacing && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setReplacing(false);
                        setDraft("");
                        save.reset();
                      }}
                      disabled={save.isPending}
                    >
                      {translate("ra.action.cancel")}
                    </Button>
                  )}
                </div>
                {save.isError && (
                  <p role="alert" className="text-sm text-destructive">
                    {translate(planeKeyErrorMessage(save.error), {
                      project:
                        (save.error as PlaneKeyError | undefined)?.project ??
                        "",
                    })}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
